import React from 'react'
import styled from 'styled-components'
import { IonIcon, IonRouterLink } from '@ionic/react'
import { bookmark } from 'ionicons/icons'
import { TextStyle } from '../../atoms/text'
import { Chip, CustomSpacer } from '../../atoms'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import { amountString } from '../../../common'

type Props = {
  page: 'HOME' | 'ASSET_HOLD' | 'SALE' | 'TRADE' | 'ASSET_FINISH'
  img: string
  artist: string
  title: string
  routerLink: string
  isFavorite?: boolean
  count?: number
  cost?: number
  saleCost?: number
  currentCost?: number
  chipDday?: number
  saleStatus?: 'RELEASE' | 'TO_BE_RELEASED'
  status?: 'VOTE' | 'FINISH' | 'SELL' | 'TO_BE_SETTLEMENT' | 'SALE' | 'TRADE'
}

/**
 * @param page 컴포넌트를 호출하는 페이지
 * @param img 카드 상단에 위치할 프로덕트 이미지
 * @param artist 작가명
 * @param title 작품명
 * @param routerLink 클릭시 이동할 상세 페이지 주소
 * @param isFavorite 관심 작품 설정 여부 (true: 설정, false: 해제)
 * @param favorite (optional) 관심 작품 설정 여부
 * @param count (optional) 프로덕트 잔량, 보유 수량, 총 거래 수량 등 표시
 * @param cost (optional) 총 구매 금액, 평단가 등
 * @param saleCost (optional) 1차 판매가
 * @param currentCost (optional) 현재가
 * @param chipDday (optional) 1차 거래 남은 판매일
 * @param saleStatus (optional) 1차 판매 상태 (ex 판매 예정, 판매 중)
 * @param status (optional) 프로덕트 상태(ex 판매중, 투표중, 매각 완료 등)
 */
export const ProductCard: React.FC<Props> = ({
  page,
  img,
  artist,
  title,
  routerLink,
  isFavorite,
  count,
  cost,
  saleCost,
  currentCost,
  chipDday,
  saleStatus,
  status,
}) => {
  // costChangeRate 구조 변경 필요 - mobx computed?
  const costChangeRateNumForTrade: number = ((Number(currentCost) - Number(saleCost)) / Number(saleCost)) * 100
  const costChangeRateNumForMyAsset: number = ((Number(currentCost) - Number(cost)) / Number(cost)) * 100

  return (
    <>
      <ItemContainer>
        <IonRouterLink routerLink={routerLink}>
          {status === 'VOTE' && <StatusBadge color={'black'}>{'투표중'}</StatusBadge>}
          {status === 'SELL' && <StatusBadge color={'black'}>{'매각중'}</StatusBadge>}
          {status === 'TO_BE_SETTLEMENT' && <StatusBadge color={'error_main'}>{'정산 예정'}</StatusBadge>}
          {status === 'FINISH' && <StatusBadge color={'success_main'}>{'정산 완료'}</StatusBadge>}
          <ImageContainer>
            {isFavorite != null && (
              <>
                {isFavorite === true && (
                  <FavoriteIconWrap>
                    <IonIcon style={{ color: '#ffffff' }} icon={bookmark} />
                  </FavoriteIconWrap>
                )}
              </>
            )}
            <LazyLoadImage
              style={
                status === 'SALE' && amountString(String(count)) === '0'
                  ? { opacity: '0.5', objectFit: 'cover' }
                  : { objectFit: 'cover' }
              }
              src={img}
              alt={title}
              effect={'blur'}
              width={'100%'}
              height={'100%'}
            />
          </ImageContainer>
          <FooterContainer>
            {/* 홈 */}
            {page === 'HOME' && (
              <>
                <ArtistContainerNoChip>
                  <p className="artist">{artist || '-'}</p>
                </ArtistContainerNoChip>
                <p className="title">{title || '-'}</p>
              </>
            )}

            {/* 마켓(조각투자) - 판매 예정) */}
            {page === 'SALE' && saleStatus === 'TO_BE_RELEASED' && (
              <>
                <ArtistContainer>
                  <p className="artist">{artist || '-'}</p>
                  <div className="chip">
                    <Chip type={'lightBlue'}>판매 예정</Chip>
                  </div>
                </ArtistContainer>
                <p className="title">{title || '-'}</p>
                <TextStyle type={'r_11'} color={'primary_dark'}>
                  판매 예정
                </TextStyle>
                <CustomSpacer spacing="5px" />
                <TextStyle type={'b_16'} color={'primary_dark'}>
                  판매 예정
                </TextStyle>
              </>
            )}

            {/* 마켓(조각투자) - 판매중) */}
            {page === 'SALE' && saleStatus === 'RELEASE' && (
              <>
                <ArtistContainer>
                  <p className="artist">{artist || '-'}</p>
                  <div className="chip">
                    <Chip type={'gray'}>{`D-${chipDday}`}</Chip>{' '}
                  </div>
                </ArtistContainer>
                <p className="title">{title || '-'}</p>
                {amountString(String(count)) === '0' ? (
                  <div style={{ color: 'red', fontSize: '20px', fontWeight: 'bold' }}>Sold out</div>
                ) : (
                  <TextStyle type={'r_11'} color={'primary_dark'}>{`${amountString(String(count))}개 남음`}</TextStyle>
                )}

                <CustomSpacer spacing="5px" />
                <TextStyle type={'b_16'} color={'primary_dark'}>{`${amountString(String(cost))}원`}</TextStyle>
              </>
            )}

            {/* 마켓(트레이드) */}
            {page === 'TRADE' && (
              <>
                <ArtistContainer>
                  <p className="artist">{artist || '-'}</p>
                  <div className="chip">
                    {costChangeRateNumForTrade > 0 ? (
                      <Chip type={'lightRed'}>{`+${amountString(String(costChangeRateNumForTrade), false, 1)}%`}</Chip>
                    ) : costChangeRateNumForTrade < 0 ? (
                      <Chip type={'lightBlue'}>{`${amountString(String(costChangeRateNumForTrade), false, 1)}%`}</Chip>
                    ) : (
                      <Chip type={'gray'}>{`${amountString(String(costChangeRateNumForTrade), false, 1)}%`}</Chip>
                    )}
                  </div>
                </ArtistContainer>
                <p className="title">{title || '-'}</p>
                <TextStyle type={'r_11'} color={'primary_dark'}>{`${amountString(String(count))}개 거래됨`}</TextStyle>
                <CustomSpacer spacing="5px" />
                <TextStyle type={'b_16'} color={'primary_dark'}>{`${amountString(String(currentCost))}원`}</TextStyle>
              </>
            )}

            {/* 마이(소유중인 자산) */}
            {page === 'ASSET_HOLD' && (
              <>
                <ArtistContainer>
                  <p className="artist">{artist || '-'}</p>
                  <div className="chip">
                    {status !== 'SALE' ? (
                      costChangeRateNumForMyAsset > 0 ? (
                        <Chip type={'lightRed'}>{`+${amountString(
                          String(costChangeRateNumForMyAsset),
                          false,
                          1,
                        )}%`}</Chip>
                      ) : costChangeRateNumForMyAsset < 0 ? (
                        <Chip type={'lightBlue'}>{`${amountString(
                          String(costChangeRateNumForMyAsset),
                          false,
                          1,
                        )}%`}</Chip>
                      ) : (
                        <Chip type={'gray'}>{`${amountString(String(costChangeRateNumForMyAsset), false, 1)}%`}</Chip>
                      )
                    ) : null}
                  </div>
                </ArtistContainer>
                <p className="title">{title || '-'}</p>
                <TextStyle type={'r_11'} color={'primary_dark'}>{`${amountString(String(count))}개 보유중`}</TextStyle>
                <CustomSpacer spacing="5px" />
                <TextStyle type={'b_16'} color={'primary_dark'}>{`${amountString(String(currentCost))}원`}</TextStyle>
              </>
            )}

            {/* 마이(매각 완료-정산 예정) */}
            {page === 'ASSET_FINISH' && status === 'TO_BE_SETTLEMENT' && (
              <>
                <ArtistContainer>
                  <p className="artist">{artist || '-'}</p>
                  <div className="chip">
                    {costChangeRateNumForMyAsset > 0 ? (
                      <Chip type={'lightRed'}>{`+${amountString(
                        String(costChangeRateNumForMyAsset),
                        false,
                        1,
                      )}%`}</Chip>
                    ) : costChangeRateNumForMyAsset < 0 ? (
                      <Chip type={'lightBlue'}>{`${amountString(
                        String(costChangeRateNumForMyAsset),
                        false,
                        1,
                      )}%`}</Chip>
                    ) : (
                      <Chip type={'gray'}>{`${amountString(String(costChangeRateNumForMyAsset), false, 1)}%`}</Chip>
                    )}
                  </div>
                </ArtistContainer>
                <p className="title">{title || '-'}</p>
                <TextStyle type={'r_11'} color={'primary_dark'}>{`${amountString(String(count))}개 보유중`}</TextStyle>
                <CustomSpacer spacing="5px" />
                <TextStyle type={'b_16'} color={'primary_dark'}>{`${amountString(String(currentCost))}원`}</TextStyle>
              </>
            )}

            {/* 마이(매각 완료-정산 완료) */}
            {page === 'ASSET_FINISH' && status === 'FINISH' && (
              <>
                <ArtistContainerNoChip>
                  <p className="artist">{artist || '-'}</p>
                </ArtistContainerNoChip>
                <p className="title">{title || '-'}</p>
              </>
            )}
          </FooterContainer>
        </IonRouterLink>
      </ItemContainer>
    </>
  )
}

const ItemContainer = styled.article`
  z-index: 1;
  font-size: 13px;
  color: #92949c;
  cursor: pointer;
`

const FooterContainer = styled.article`
  white-space: nowrap;
  .artist {
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--ion-color-primary_light);
    margin: 0;
    font-size: 13px;
    font-weight: 500;
  }
  .title {
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 5px 0;
    font-size: 15px;
    font-weight: 500;
    color: var(--ion-color-primary_dark);
  }
`

const ArtistContainer = styled.span`
  display: flex;
  justify-content: space-between;
  .artist {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--ion-color-primary_light);
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    width: 58%;
  }
`

const ArtistContainerNoChip = styled.span`
  position: relative;
  margin-top: 10px;
  height: 19px;
  .artist {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--ion-color-primary_light);
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    width: 100%;
  }
`

const ImageContainer = styled.div`
  z-index: 1;
  width: 100%;
  border-radius: 7px;
  aspect-ratio: 1/1;
  object-fit: cover;
  position: relative;
`

const StatusBadge = styled.div<{ color?: 'error_main' | 'black' | 'success_main' }>`
  z-index: 2;
  position: absolute;
  margin: 8px;
  padding: 2px 8px;
  border-radius: 8px;
  color: #ffffff;
  background-color: ${(props) =>
    props.color === 'error_main' ? '#EA4E3D' : props.color === 'success_main' ? '#4c7eff' : '#1a1f27'};
`

const FavoriteIconWrap = styled.div<{ color?: 'error_main' | 'black' | 'success_main' }>`
  position: absolute;
  width: 100%;
  z-index: 2;
  top: -5px;
  right: 3px;
  ion-icon {
    font-size: 35px;
    float: right;
    z-index: 2;
  }
`
